import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  Table,
} from '@tanstack/react-table';
import {
  IAnalyticRubricCriterion,
  IAnalyticRubricLevel, useQuestionRubric,
} from '@/components/staffer/ExerciseBuilder/Rubric/useQuestionRubric';
import {
  createAnalyticRubricColumns,
} from '../AnalyticRubric.utils';
import EditorCriterionCell from '../EditorCriterionCell';
import EditorLevelDescriptionCell from '../EditorLevelDescriptionCell';

type UseAnalyticRubricEditorArgs = {
  questionId: number | undefined;
  locale?: Lowercase<Locale>;
};

interface UseAnalyticRubricEditorReturn {
  data: IAnalyticRubricCriterion[];
  deleteCriterion: (criterionIndex: number) => void;
  setData: React.Dispatch<React.SetStateAction<IAnalyticRubricCriterion[]>>;
  table: Table<IAnalyticRubricCriterion>;
}

const DEFAULT_CRITERIA_NAMES = ['', '', '', ''];
const DEFAULT_SCORE_LEVELS = [4, 3, 2, 1];

const createDefaultLevel = (score: number): IAnalyticRubricLevel => ({
  description_en: '',
  description_es: '',
  description_id: null,
  level_id: null,
  score,
});

export const createDefaultCriterion = (
  name: string,
  existingLevels?: IAnalyticRubricLevel[],
): IAnalyticRubricCriterion => ({
  criterion_id: null,
  levels: existingLevels ?? DEFAULT_SCORE_LEVELS.map(createDefaultLevel),
  name_en: name,
  name_es: '',
});

const DEFAULT_ANALYTIC_RUBRIC_DATA: IAnalyticRubricCriterion[] = DEFAULT_CRITERIA_NAMES.map(
  name => createDefaultCriterion(name),
);

const useAnalyticRubricEditor = ({
  questionId,
  locale = 'en',
}: UseAnalyticRubricEditorArgs): UseAnalyticRubricEditorReturn => {
  const [data, setData] = useState<IAnalyticRubricCriterion[]>(DEFAULT_ANALYTIC_RUBRIC_DATA);

  const { data: fetchedData, isLoading } = useQuestionRubric({ questionId });

  useEffect(() => {
    if (!isLoading && fetchedData && fetchedData?.rubric_type === 'analytic') {
      setData(fetchedData.rubric_data as IAnalyticRubricCriterion[]);
    }
  }, [fetchedData, isLoading]);

  const handleCriterionNameChange = useCallback(
    (criterionIndex: number, newName: string) => {
      setData(prevData => prevData.map((criterion, idx) => {
        if (idx === criterionIndex) {
          return {
            ...criterion,
            [locale === 'en' ? 'name_en' : 'name_es']: newName,
          };
        }
        return criterion;
      }));
    },
    [locale],
  );

  const handleLevelDescriptionChange = useCallback(
    (criterionIndex: number, score: number, newDescription: string) => {
      setData(prevData => prevData.map((criterion, index) => {
        if (index === criterionIndex) {
          return {
            ...criterion,
            levels: criterion.levels.map((level) => {
              if (level.score === score) {
                return {
                  ...level,
                  [locale === 'en' ? 'description_en' : 'description_es']: newDescription,
                };
              }
              return level;
            }),
          };
        }
        return criterion;
      }));
    },
    [locale],
  );

  const deleteCriterion = useCallback(
    (criterionIndex: number) => {
      setData(prevData => prevData.filter((_, idx) => idx !== criterionIndex));
    },
    [],
  );

  const columns = useMemo(
    () => createAnalyticRubricColumns({
      CriterionCell: EditorCriterionCell,
      LevelCell: EditorLevelDescriptionCell,
      cellProps: {
        onCriterionNameChange: handleCriterionNameChange,
        onDeleteCriterion: deleteCriterion,
        onLevelDescriptionChange: handleLevelDescriptionChange,
        totalCriteria: data.length,
      },
      locale,
      scoreLevels: DEFAULT_SCORE_LEVELS,
    }),
    [locale, handleCriterionNameChange, deleteCriterion, handleLevelDescriptionChange, data.length],
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  return { data, deleteCriterion, setData, table };
};

export default useAnalyticRubricEditor;
